import { z } from 'zod';
import { disciplineSchema } from './payer.js';
import { restricted } from './redaction.js';
import { credentialCategorySchema } from './workforce.js';
/**
 * Credential types and per-caregiver credentials (§6.3.5, slice-02 §4).
 *
 * ## Two statuses, and only one of them decides anything
 *
 * `caregiver_credential.status` is a **cached** value, recomputed nightly by
 * §17.2's job. §6.3.5 is explicit that it must never be trusted alone for a
 * blocking decision, and that is **R-2b**.
 *
 * So this file carries both, named so they cannot be confused:
 *
 *   - `cachedStatus` — what the column says. For lists, dashboards, sorting and
 *     counting. Stale by construction: computed against last night's date,
 *     which is the wrong date for every service date but one.
 *   - `evaluation` — what `evaluateCredentialCompliance` says, right now,
 *     against the date being asked about. This is what refuses.
 *
 * A response that carried one field called `status` would make the wrong one
 * the obvious thing to read, and the wrong one to read is the one that lets an
 * expired credential through.
 */
export const credentialStatusSchema = z.enum([
    'PENDING',
    'VALID',
    'EXPIRING_SOON',
    'EXPIRED',
    'REJECTED',
]);
// ---------------------------------------------------------------------------
// credential_type
// ---------------------------------------------------------------------------
export const credentialTypeSchema = z.object({
    id: z.string().uuid(),
    key: z.string(),
    name: z.string(),
    category: credentialCategorySchema,
    isRequiredForDisciplines: z.array(disciplineSchema),
    blocksScheduling: z.boolean(),
    renewalPeriodMonths: z.number().int().nullable(),
    warnDaysBefore: z.array(z.number().int()),
    isActive: z.boolean(),
});
export const credentialTypeListSchema = z.object({
    credentialTypes: z.array(credentialTypeSchema),
});
export const createCredentialTypeSchema = z.object({
    /** Stable, and how code finds a well-known type. Lower-cased on write. */
    key: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z0-9_]+$/, 'Letters, numbers and underscores only.'),
    name: z.string().trim().min(1).max(200),
    category: credentialCategorySchema,
    isRequiredForDisciplines: z.array(disciplineSchema).max(7).default([]),
    /**
     * Whether a failure refuses a shift or merely warns.
     *
     * No default. Marking everything blocking is the safe-looking choice and the
     * wrong one — an agency whose caregivers are all blocked by a missing
     * in-service hour stops using the feature, and then nothing blocks anything.
     * Making it explicit means somebody decided.
     */
    blocksScheduling: z.boolean(),
    renewalPeriodMonths: z.number().int().positive().max(120).nullish(),
    /** §6.3.5's default. Every value is days before expiry, so all are positive. */
    warnDaysBefore: z
        .array(z.number().int().positive().max(1_000))
        .max(10)
        .default([90, 60, 30, 14, 7]),
});
/** `key` is absent: it is what everything else refers to and does not move. */
export const updateCredentialTypeSchema = createCredentialTypeSchema
    .omit({ key: true })
    .partial()
    .extend({ isActive: z.boolean().optional() })
    .strict();
// ---------------------------------------------------------------------------
// caregiver_credential
// ---------------------------------------------------------------------------
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2027-03-11 — four digits, two, two.');
export const caregiverCredentialSchema = z.object({
    id: z.string().uuid(),
    credentialTypeId: z.string().uuid(),
    credentialTypeName: z.string(),
    blocksScheduling: z.boolean(),
    identifier: z.string().nullable(),
    issuedDate: z.string().nullable(),
    expiresAt: z.string().nullable(),
    /**
     * The stored column, named so nobody reaches for it to decide anything.
     *
     * R-2b: this is for lists and dashboards. Anything that refuses calls
     * `GET /v1/caregivers/:id/compliance`, which evaluates live.
     */
    cachedStatus: credentialStatusSchema,
    /**
     * Days from today until it lapses, negative once it has.
     *
     * Computed at read time rather than cached, so a credential that expires
     * overnight does not read "expires in 1 day" all of the next morning. This is
     * what makes expiry visible *before* it lapses, which is the half of §17.2
     * that a status of EXPIRED cannot express.
     */
    daysUntilExpiry: z.number().int().nullable(),
    /** True when `daysUntilExpiry` is inside the type's widest warning window. */
    expiringSoon: z.boolean(),
    hasDocument: z.boolean(),
    verifiedBy: z.string().uuid().nullable(),
    verifiedAt: z.string().nullable(),
    rejectionReason: z.string().nullable(),
    createdAt: z.string(),
});
export const caregiverCredentialListSchema = z.object({
    credentials: z.array(caregiverCredentialSchema),
});
export const recordCredentialSchema = z.object({
    credentialTypeId: z.string().uuid(),
    identifier: z.string().trim().max(120).nullish(),
    issuedDate: isoDate.nullish(),
    /**
     * The last day it is valid, inclusive.
     *
     * Nullable because an I-9 does not expire, and because a licence whose
     * renewal the agency does not track has no date worth inventing.
     */
    expiresAt: isoDate.nullish(),
});
export const verifyCredentialSchema = z.object({
    note: z.string().trim().max(2_000).nullish(),
});
export const rejectCredentialSchema = z.object({
    /**
     * Required, and not defaulted.
     *
     * A rejection with no reason is one a caregiver answers by re-uploading the
     * same unreadable photograph. §17.2 gives them a direct upload path
     * specifically because the friction of "email a photo to the office" is why
     * credentials lapse — and a rejection they cannot act on reintroduces it.
     */
    reason: z.string().trim().min(1).max(2_000),
});
// ---------------------------------------------------------------------------
// The live evaluation — the thing that refuses
// ---------------------------------------------------------------------------
export const credentialIssueSchema = z.object({
    credentialTypeId: z.string().uuid(),
    credentialTypeName: z.string(),
    reason: z.enum(['MISSING', 'EXPIRED', 'EXPIRING', 'UNVERIFIED', 'REJECTED']),
    credentialId: z.string().uuid().nullable(),
    expiresAt: z.string().nullable(),
    daysUntilExpiry: z.number().int().nullable(),
    /** Written for the coordinator being refused. Never a code or a field name. */
    message: z.string(),
});
export const complianceSchema = z.object({
    caregiverId: z.string().uuid(),
    /** The date the answer is for. The **service** date, not today. */
    asOfDate: z.string(),
    compliant: z.boolean(),
    blocking: z.array(credentialIssueSchema),
    warnings: z.array(credentialIssueSchema),
});
export const complianceQuerySchema = z.object({
    /**
     * The service date. Defaults to today in the agency's timezone.
     *
     * A caller that omits it gets "can they work today", which is the right
     * default and the wrong question for scheduling — the scheduling slice always
     * sends the date of the shift it is trying to book. §17.1's whole point is
     * that those two answers differ.
     */
    asOf: isoDate.optional(),
});
// ---------------------------------------------------------------------------
// The compliance queue — HR's morning screen (slice-02 session 8)
// ---------------------------------------------------------------------------
export const queueOrderingKeySchema = z.enum([
    'BLOCKING',
    'WORK_AT_STAKE',
    'SOONEST_EXPIRY',
    'NAME',
]);
export const complianceQueueEntrySchema = z.object({
    rank: z.number().int().positive(),
    caregiverId: z.string().uuid(),
    caregiverName: restricted(z.string().nullable(), 'caregiver:read'),
    /**
     * ACTIVE, or ONBOARDING with the checklist clear and only a credential in
     * the way — the second is someone HR can unblock today, and the screen says
     * which kind of row it is.
     */
    caregiverStatus: z.enum(['ACTIVE', 'ONBOARDING']),
    cannotWorkToday: z.boolean(),
    /** The first day they cannot work: today, or the day after a lapse. */
    blockedFrom: z.string().nullable(),
    /** What put them here: today's blocking issues, or the expiring ones. */
    issues: z.array(credentialIssueSchema),
    workAtStake: z.object({
        minutes: z.number().int().nonnegative(),
        from: z.string().nullable(),
        days: z.array(z.object({ date: z.string(), minutes: z.number().int().positive() })),
    }),
    soonestExpiry: z.string().nullable(),
    /**
     * Composed on the server beside the comparator that ordered the queue, so
     * the reason given is the key that decided. See `compliance-queue.ts` in
     * `@homecare/domain`.
     */
    explanation: z.object({
        listed: z.string(),
        atStake: z.string(),
        placement: z.string(),
        decidedBy: queueOrderingKeySchema.nullable(),
    }),
});
export const complianceQueueSchema = z.object({
    /** Today, in the agency's timezone. */
    asOfDate: z.string(),
    horizon: z.object({ from: z.string(), to: z.string() }),
    /**
     * What "work at stake" counts. AVAILABILITY until the scheduling slice
     * exists: hours offered to the rota, not hours booked. Sent so the screen
     * can say which, rather than letting a reader assume visits.
     */
    basis: z.literal('AVAILABILITY'),
    entries: z.array(complianceQueueEntrySchema),
});
//# sourceMappingURL=credential.js.map